/**
 * Story units: scene-count-based progress with choice averaging.
 * Used by story nav to position the track and chapters by "number of scenes between points".
 */

export interface SceneForUnits {
  id: string
  type?: string
  choices?: {
    choice1: { result: string | { sceneId: string } | Array<{ sceneId: string }> }
    choice2: { result: string | { sceneId: string } | Array<{ sceneId: string }> }
  }
}

function getResultSceneIds(
  result: string | { sceneId: string } | Array<{ sceneId: string }>
): string[] {
  if (typeof result === 'string') return [result]
  if (Array.isArray(result)) return result.map((r) => r.sceneId)
  return [result.sceneId]
}

function indexOfSceneById(scenes: SceneForUnits[], id: string): number {
  const i = scenes.findIndex((s) => s.id === id)
  return i >= 0 ? i : -1
}

/**
 * Average number of "scene units" from sceneIndex to the end of the story.
 * At choices, takes (branch1Length + branch2Length) / 2.
 */
function getAverageLengthFrom(
  sceneIndex: number,
  scenes: SceneForUnits[],
  memo: Map<number, number>
): number {
  if (sceneIndex >= scenes.length) return 0
  const cached = memo.get(sceneIndex)
  if (cached !== undefined) return cached

  const scene = scenes[sceneIndex]
  let length: number

  if (scene.choices?.choice1 && scene.choices?.choice2) {
    const ids1 = getResultSceneIds(scene.choices.choice1.result)
    const ids2 = getResultSceneIds(scene.choices.choice2.result)
    const branch1Length =
      ids1.length > 0
        ? ids1
            .map((id) => getAverageLengthFrom(indexOfSceneById(scenes, id), scenes, memo))
            .reduce((a, b) => a + b, 0) / ids1.length
        : 0
    const branch2Length =
      ids2.length > 0
        ? ids2
            .map((id) => getAverageLengthFrom(indexOfSceneById(scenes, id), scenes, memo))
            .reduce((a, b) => a + b, 0) / ids2.length
        : 0
    length = 1 + (branch1Length + branch2Length) / 2
  } else {
    length = 1 + getAverageLengthFrom(sceneIndex + 1, scenes, memo)
  }

  memo.set(sceneIndex, length)
  return length
}

export interface StoryUnitsResult {
  /** Total story length in scene units (with choice averaging). */
  totalSceneUnits: number
  /** Cumulative units from start to each chapter (by story order). cumulativeUnitsToChapter[i] = units from start to chapter i. */
  cumulativeUnitsToChapter: number[]
}

/**
 * Compute total story units and cumulative units up to each chapter.
 * @param scenes - Full scenes array from story
 * @param chapterSceneIndices - For each chapter, its scene index in the story
 */
export function computeStoryUnits(
  scenes: SceneForUnits[],
  chapterSceneIndices: number[]
): StoryUnitsResult {
  const memo = new Map<number, number>()
  const totalSceneUnits = getAverageLengthFrom(0, scenes, memo)

  const cumulativeUnitsToChapter = chapterSceneIndices.map((sceneIndex) => {
    const fromHereToEnd = getAverageLengthFrom(sceneIndex, scenes, memo)
    return totalSceneUnits - fromHereToEnd
  })

  return { totalSceneUnits, cumulativeUnitsToChapter }
}
